'use client';

import { type HTMLAttributes, useMemo } from 'react';

import { Tag } from '@/app/components/shared/dealer/components/shared/tag';
import {
  DEALER_DEFAULT_VALUE,
  DEALER_SEARCH_KEY,
  DEALER_TYPES,
} from '@/app/components/shared/dealer/utils/dealer-constant';
import { createDealerNameFormat } from '@/app/components/shared/dealer/utils/format';
import {
  isDealerActiveAfterSale,
  isDealerActiveBodyPaint,
  isDealerActiveSale,
} from '@/app/components/shared/dealer/utils/mapping-dealer';
import { EVENT_NAME } from '@/app/components/shared/dealer/utils/tracking';
import type { DealerSearch } from '@/app/components/shared/dealer/utils/types';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
import { safeCall } from '@/app/utils/safeCall';

interface CardLocationProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'onClick'> {
  size?: 'sm' | 'md';
  dealer: DealerSearch;
  onClick?: (dealer: DealerSearch, type?: string) => void;
}

export const CardLocation = ({
  size = 'md',
  className,
  dealer,
  onClick,
  ...props
}: CardLocationProps) => {
  const isShowShowroom = isDealerActiveSale(dealer);
  const isShowService = isDealerActiveAfterSale(dealer);
  const isShowBodyPaint = isDealerActiveBodyPaint(dealer);

  const name = useMemo(() => {
    if (isShowShowroom) return dealer.sale?.name_th;
    if (isShowService) return dealer.aftersale?.name_th;
    if (isShowBodyPaint) return dealer.bp?.name_th;
    return;
  }, [dealer, isShowBodyPaint, isShowService, isShowShowroom]);

  const address = useMemo(() => {
    if (isShowShowroom) return dealer.sale?.address;
    if (isShowService) return dealer.aftersale?.address;
    if (isShowBodyPaint) return dealer.bp?.address;
    return;
  }, [dealer, isShowBodyPaint, isShowService, isShowShowroom]);

  const handleOnClickItem = () => {
    safeCall(onClick, dealer);
  };

  const handleOnClickTag = (typeId: string) => {
    safeCall(onClick, dealer, typeId);
  };

  return (
    <div
      className={cn(
        'bg-silver-50 hover:bg-silver-100 cursor-pointer rounded-sm p-3',
        className
      )}
      onClick={() => {
        handleOnClickItem();
      }}
      {...props}
    >
      <div className="flex gap-x-2">
        <div>
          <p
            className={cn('font-noto-bold where-span:text-inline-wrap', {
              'text-body-2-fixed': size === 'sm',
              'text-body-1-fixed': size === 'md',
            })}
            {...createDataAttributes({ test: 'name' })}
            dangerouslySetInnerHTML={{
              __html: createDealerNameFormat(name),
            }}
          />
          <p
            className={cn('text-gray-800', {
              'text-caption': size === 'sm',
              'text-body-2-fixed': size === 'md',
            })}
            {...createDataAttributes({ test: 'address' })}
          >
            {address || DEALER_DEFAULT_VALUE.ADDRESS}
          </p>
        </div>
        {size === 'md' && !!dealer.distance && (
          <div
            className="rounded-2xs ml-auto h-fit shrink-0 bg-white p-2"
            {...createDataAttributes({ test: 'distance' })}
          >
            <strong>{dealer.distance.toFixed(1)}</strong> กม.
          </div>
        )}
      </div>

      <div className="mt-2 flex gap-x-2">
        {DEALER_TYPES.map((type, index) => {
          if (!isShowShowroom && type.id === DEALER_SEARCH_KEY.Showroom)
            return null;
          if (!isShowService && type.id === DEALER_SEARCH_KEY.Service)
            return null;
          if (!isShowBodyPaint && type.id === DEALER_SEARCH_KEY.BodyPaint)
            return null;

          return (
            <Tag
              key={type.id}
              className="text-body-2-fixed"
              onChange={() => {
                handleOnClickTag(type.id);
              }}
              {...createDataAttributes({ test: `tag-${index}` })}
              {...createDataGA({
                click: true,
                eventName: EVENT_NAME,
                group: 'components/shared/dealer/shared/card-location',
                value: type.id,
                text: type.label,
              })}
            >
              {type.label}
            </Tag>
          );
        })}
      </div>
    </div>
  );
};
